import { useEffect, useState } from 'react';
import { spotifyApi } from '@/lib/api';
import type { CurrentlyPlaying, Track } from '@/types';

interface UseCurrentTrackReturn {
  currentTrack: Track | null;
  isPlaying: boolean;
  progressMs: number;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch and track the currently playing Spotify track
 * Polls every 5 seconds when a track is playing
 */
export const useCurrentTrack = (pollingInterval: number = 5000): UseCurrentTrackReturn => {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressMs, setProgressMs] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCurrentTrack = async () => {
    try {
      const playbackState: CurrentlyPlaying = await spotifyApi.playback.getCurrentTrack();

      setCurrentTrack(playbackState.track);
      setIsPlaying(playbackState.is_playing);
      setProgressMs(playbackState.progress_ms);
      setError(null);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching current track:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchCurrentTrack();

    // Set up polling - always poll to detect state changes
    const intervalId = setInterval(() => {
      fetchCurrentTrack();
    }, pollingInterval);

    return () => clearInterval(intervalId);
  }, [pollingInterval]);

  return {
    currentTrack,
    isPlaying,
    progressMs,
    isLoading,
    error,
    refresh: fetchCurrentTrack,
  };
};
